import React, { useState } from 'react';
import { ChartBar, ChartColumn, ChartPie, ChartLine, BarChart3 } from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import Tooltip from '../tooltip';

interface ChartSeries {
  name: string;
  data: number[];
  stack?: string;
}

interface UniversalChartData {
  type: 'line' | 'bar' | 'column' | 'stackedColumn' | 'pie';
  title?: string;
  subtitle?: string;
  categories: string[];
  series: ChartSeries[];
  xAxisTitle?: string;
  yAxisTitle?: string;
  stacking?: 'normal';
  showLegend?: boolean;
  colors?: string[];
}

interface ChartTypeOption {
  type: string;
  icon: LucideIcon;
  label: string;
  color: string;
}

interface ChartSwitcherProps {
  defaultType: string | null | undefined;
  currentType: string;
  data: UniversalChartData;
  onTypeChange: (newType: string) => void;
}

const ChartSwitcher: React.FC<ChartSwitcherProps> = ({
  defaultType,
  currentType, 
  data, 
  onTypeChange 
}) => {
  const [showOptions, setShowOptions] = useState(false);
  
  const getAvailableChartTypes = (): ChartTypeOption[] => {
    const types: ChartTypeOption[] = [];

    if (!data || !data.series || !data.categories) {
      return types;
    }

    if (defaultType === 'linechart') {
      // types.push({
      //   type: 'piechart',
      //   icon: FaChartPie,
      //   label: 'Pie Chart',
      //   color: 'text-primary-text'
      // });

      // types.push({
      //   type: 'linechart',
      //   icon: FaChartLine,
      //   label: 'Line Chart',
      //   color: 'text-primary-text'
      // });
    } else {
      types.push({
        type: 'barchart',
        icon: ChartBar,
        label: 'Bar Chart',
        color: 'text-primary-text'
      });
      
      types.push({
        type: 'columnchart',
        icon: ChartColumn,
        label: 'Column Chart', 
        color: 'text-primary-text'
      });


      if (data.series.length === 1 || data.categories.length <= 10) {
        types.push({
          type: 'piechart',
          icon: ChartPie,
          label: 'Pie Chart',
          color: 'text-primary-text'
        });
      }

      const hasTimeData = (): boolean => {
        if (!data.categories || data.categories.length < 2) return false;

        const timePatterns: RegExp[] = [
          /^\d{4}$/,                    // Year: 2024
          /^\d{4}-\d{2}$/,             // Year-Month: 2024-01
          /^\d{4}-\d{2}-\d{2}$/,       // Date: 2024-01-15
          /^\d{4}-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/i, // 2024-Jan
          /^Q[1-4]\s*\d{4}$/i,         // Quarter: Q1 2024
          /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i, // Month names
          /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,  // MM/DD/YYYY
          /^\d{1,2}-\d{1,2}-\d{2,4}$/,    // MM-DD-YYYY
          /^Week\s*\d+/i,              // Week 1, Week 2
          /^\d{4}\s*(H1|H2)$/i,        // 2024 H1
          /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)/i // Day names
        ];
        
        const matchingPatterns = data.categories.filter((category: string) => {
          if (!category) return false;
          return timePatterns.some((pattern: RegExp) => pattern.test(category.trim()));
        });
        
        const isTimeData = matchingPatterns.length > data.categories.length * 0.7;

        const parseableAsDate = data.categories.filter((category: string) => {
          if (!category) return false;
          const parsed = new Date(category);
          return !isNaN(parsed.getTime());
        });
        
        const isDateData = parseableAsDate.length > data.categories.length * 0.5;
        
        return isTimeData || isDateData;
      };

      if (hasTimeData()) {
        types.push({
          type: 'linechart',
          icon: ChartLine,
          label: 'Line Chart',
          color: 'text-primary-text hover:text-purple-800'
        });
      }
    }

    return types;
  };

  const availableTypes: ChartTypeOption[] = getAvailableChartTypes();

  const handleTypeChange = (newType: string) => {
    onTypeChange(newType);
    setShowOptions(false);
  };

  // Map current chart type to display type for comparison  
  const normalizeType = (type: string): string => {
    // No normalization needed since we're using the exact same names
    return type;
  };

  const normalizedCurrentType = normalizeType(currentType);

  return (
    <div className="absolute top-3 right-3 z-10">
      <div className="relative">
        <Tooltip message="Change chart type" >
          <button
            type="button"
            onClick={() => setShowOptions(!showOptions)}
            className={`p-2 group flex items-center justify-center rounded-full transition-colors ${showOptions ? 'text-primary' : 'text-primary-text hover:bg-secondary-background hover:text-primary'}`}
          >
            <BarChart3 className="w-5 h-5" strokeWidth={1.75}/>
          </button>
        </Tooltip>

        {showOptions && (
          <div className="absolute top-8 right-0 bg-white border border-gray-200 rounded-3xl shadow-lg min-w-[200px] z-20">
            <div className="p-2">
              <div className="space-y-1">
                {availableTypes.map((chartType, index) => {
                  const Icon = chartType.icon;
                  const isActive = normalizedCurrentType === chartType.type;

                  return (
                    <button
                      key={`${chartType.type}-${index}`}
                      onClick={() => handleTypeChange(chartType.type)}
                      className={`
                        w-full flex items-center gap-2 px-3 py-2 text-sm rounded-3xl transition-colors
                        ${isActive 
                          ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                          : 'hover:bg-blue-50 text-gray-700 border-blue-200'
                        }
                      `}
                    >
                      <Icon className={`w-4 h-4  ${isActive ? 'text-primary' : chartType.color}`} />
                      <span>{chartType.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {showOptions && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => setShowOptions(false)}
        />
      )}
    </div>
  );
};

export default ChartSwitcher;